<template>
  <div>
    <form-alert :message="alertMessage" :type="alertType" />
    <div
      :class="[
        'draws-table__row',
        { scratched: !draw.is_active, 'multi-judge-row': judgesNum > 1 }
      ]"
    >
    <div :class="['draws-table__col --names', { 'extra_names_width': roundType !== 1 }]">
      <div class="draws-table__names-group">
        <div class="draws-table__lead">
          <span v-if="!draw.is_published" class="sort-handle sortable-arrows drag-handle" />
          <span :class="['row-number', { 'pl-2': draw.is_published }]">
            {{ draw.order + 1 }}.
          </span>
        </div>
        <div class="draws-table__cell draws-table__cell--member">
          {{ memberName }}
          <template v-if="draw.member && draw.member.deleted">
            <br><em style="color: var(--primary); font-size: 0.7em">(deleted)</em>
          </template>
          <template v-if="eventLabel">
            <br><em style="color: var(--primary); font-size: 0.7em">(events: {{ eventLabel }})</em>
          </template>
        </div>
        <div class="draws-table__cell draws-table__cell--horse">
          {{ draw.horse != null ? draw.horse.name : '' }}
          <template v-if="draw.horse && draw.horse.deleted">
            <br><em style="color: var(--primary); font-size: 0.7em">(deleted)</em>
          </template>
        </div>
      </div>
      <div class="draws-table__cell actions nomination_status">
        <template v-if="hasMember">
          <app-multiselect
            :key="'move-' + draw.id + '-' + draw.order"
            class="multiselect--sm draws-table__move-select"
            :value="null"
            :options="moveOptions"
            label="label"
            track-by="value"
            :searchable="false"
            :allow-empty="true"
            placeholder="Move to"
            @input="onMoveSelect"
          />
          <app-multiselect
            class="multiselect--sm draws-table__commit-select"
            :value="currentCommitOption"
            :options="commitOptionsWithNull"
            label="label"
            track-by="value"
            :searchable="false"
            :allow-empty="false"
            placeholder="-"
            @input="onCommitChange"
          />
          <button
            class="draws-table__scratch btn-warning"
            type="button"
            @click="$emit('scratch', draw)"
          >
            <i class="scratch_draw" />
          </button>
          <i
            :class="['pay_status', isPaid ? 'paid' : 'unpaid']"
            @click="$emit('toggle-payment', draw)"
          />
        </template>
      </div>
    </div>
    <div class="draws-table__scores">
    <div class="draws-table__col --inputs">
      <div v-for="judgeNumber in judgeNumbersList()" :key="judgeNumber" class="judge-scores-row">
        <div v-for="(limit, limitIndex) in scoreLimits" :key="limit.type" class="draws-table__cell position-relative">
          <label v-if="judgeNumber === 1" class="row-label draws-table__label">{{ limit.label }}</label>
          <input
            type="text"
            placeholder="-"
            :value="judgeValue(judgeNumber, limit.type)"
            :disabled="scoreDisabled || isFieldPending(judgeNumber, limit.type)"
            :class="{ disabled: scoreDisabled || isFieldPending(judgeNumber, limit.type) }"
            data-score-input
            :data-limit-index="limitIndex"
            :data-judge-index="judgeNumber"
            @input="maskScore(judgeNumber, limit, $event)"
            @change="emitMark(judgeNumber, limit, $event)"
            @keydown="onScoreKeydown($event)"
          >
          <span v-if="isFieldPending(judgeNumber, limit.type)" class="spinner-border spinner-border-sm position-absolute" style="right: 8px; top: 50%; margin-top: -7px" />
        </div>
      </div>
      <div v-if="judgesNum > 1" class="judge-scores-row judge-scores-row--average">
        <div v-for="limit in scoreLimits" :key="limit.type" class="draws-table__cell">
          {{ averagePreviewValues()[limit.type] }}
        </div>
      </div>
    </div>
    <div class="draws-table__totals-group">
      <div class="draws-table__col --total">
        <div class="row-label draws-table__label">{{ totalLabel }}</div>
        <div class="draws-table__total-value">{{ formatScore(draw.total) }}</div>
      </div>
      <div v-if="roundType >= 2" class="draws-table__col --total">
        <div class="row-label draws-table__label">Total score</div>
        <div class="draws-table__total-value">{{ formatScore(draw.totals_sum) }}</div>
      </div>
    </div>
    </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, reactive, watch, set } from '@vue/composition-api'
import AppMultiselect from '@/components/AppMultiselect'
import FormAlert from '@/components/FormAlert'

export default defineComponent({
  name: 'DrawRow',
  components: { AppMultiselect, FormAlert },
  props: {
    draw: { type: Object, required: true },
    roundType: { type: Number, default: 1 },
    pendingMarks: { type: Object, default: () => ({}) },
    emptySlots: { type: Array, default: () => [] },
    commitOptions: { type: Array, default: () => [] },
    scoreLimits: { type: Array, default: () => [] },
    judgesNum: { type: Number, default: 1 },
    alertMessage: { type: String, default: null },
    alertType: { type: String, default: 'warning' }
  },
  setup(props, { emit }) {
    const judgeInputs = reactive({})
    const dirtyFields = reactive({})

    function judgeNumbersList() {
      const count = Math.max(1, props.judgesNum)
      return Array.from({ length: count }, (_, i) => i + 1)
    }

    function ensureJudgeSlot(judgeNumber) {
      if (judgeInputs[judgeNumber] == null) set(judgeInputs, judgeNumber, {})
    }

    function findJudgeScore(draw, judgeNumber) {
      const scores = draw.judge_scores
      if (!Array.isArray(scores)) return null
      return scores.find(s => s.judge_number === judgeNumber) || null
    }

    // Rounds to 2dp for precision but drops trailing zeros (34.00 -> "34", 34.50 -> "34.5"),
    // matching the monolith's `(float)`/`parseFloat(...).toString()` display behaviour.
    function toDisplayScore(value) {
      return value == null ? null : String(parseFloat(Number(value).toFixed(2)))
    }

    function syncJudgeValues(draw, limits, numbers) {
      numbers.forEach((judgeNumber) => {
        ensureJudgeSlot(judgeNumber)
        const existing = findJudgeScore(draw, judgeNumber)
        limits.forEach((limit) => {
          const key = `${judgeNumber}_${limit.type}`
          if (dirtyFields[key]) return
          let val = ''
          if (existing != null && existing[limit.type] != null) {
            val = toDisplayScore(existing[limit.type])
          } else if (judgeNumber === 1 && numbers.length === 1 && draw[limit.type] != null) {
            val = toDisplayScore(draw[limit.type])
          }
          set(judgeInputs[judgeNumber], limit.type, val)
        })
      })
    }

    watch(() => props.draw, (draw) => {
      syncJudgeValues(draw, props.scoreLimits, judgeNumbersList())
    }, { immediate: true, deep: true })

    watch(() => [props.scoreLimits, props.judgesNum], () => {
      syncJudgeValues(props.draw, props.scoreLimits, judgeNumbersList())
    })

    function judgeValue(judgeNumber, type) {
      return judgeInputs[judgeNumber] != null && judgeInputs[judgeNumber][type] != null
        ? judgeInputs[judgeNumber][type]
        : ''
    }

    const memberName = computed(() => {
      const m = props.draw.member
      if (m == null) return ''
      return `${m.first_name} ${m.last_name}`
    })

    const eventLabel = computed(() => {
      const nom = props.draw.nomination
      if (nom == null) return null
      if (nom.comb_nomination_id != null) return 'All'
      if (nom.current_event_id != null) {
        return nom.event_name != null ? nom.event_name : String(nom.current_event_id)
      }
      return nom.event_name != null ? nom.event_name : null
    })

    const hasMember = computed(() => props.draw.member != null)

    const totalLabel = computed(() => {
      if (props.roundType === 3) return 'Final score'
      if (props.roundType === 4) return 'Run-off score'
      return 'Round score'
    })

    function formatScore(value) {
      return value != null ? toDisplayScore(value) : '0'
    }

    const scoreDisabled = computed(() => !props.draw.user_id || !props.draw.is_active || !props.draw.is_published)

    function isFieldPending(judgeNumber, fieldType) {
      const key = `${props.draw.id}_${judgeNumber}_${fieldType}`
      return !!props.pendingMarks[key]
    }

    const isPaid = computed(() => {
      const nom = props.draw.nomination
      return nom != null && nom.is_paid
    })

    const moveOptions = computed(() => props.emptySlots.map(s => ({ value: s.id, label: String(s.order) })))

    const commitOptionsWithNull = computed(() => [{ value: null, label: 'Payment type' }].concat(props.commitOptions))

    const currentCommitOption = computed(() => {
      const nom = props.draw.nomination
      const val = nom != null ? nom.commit : null
      return commitOptionsWithNull.value.find(o => o.value === val) || commitOptionsWithNull.value[0]
    })

    function onCommitChange(option) {
      if (option == null) return
      emit('commit', { nominationId: props.draw.nomination_id, commit: option.value })
    }

    function onMoveSelect(option) {
      if (option == null) return
      emit('move', {
        drawId: props.draw.id,
        data: {
          move_on: option.value,
          current_order: props.draw.order,
          event_id: props.draw.event_id,
          round_id: props.draw.round
        }
      })
    }

    function maskScore(judgeNumber, limit, event) {
      const el = event.target
      let val = el.value.replace(/[^0-9.]/g, '')
      const parts = val.split('.')
      if (parts.length > 2) val = `${parts[0]}.${parts.slice(1).join('')}`
      if (parts.length === 2 && parts[1].length > 2) {
        val = `${parts[0]}.${parts[1].substring(0, 2)}`
      }
      if (val !== '' && val !== '.' && Number(val) > limit.max) {
        val = String(limit.max)
      }
      if (el.value !== val) {
        el.value = val
      }
      ensureJudgeSlot(judgeNumber)
      set(judgeInputs[judgeNumber], limit.type, val)
      dirtyFields[`${judgeNumber}_${limit.type}`] = true
    }

    function emitMark(judgeNumber, limit, event) {
      const val = event.target.value.trim()
      const score = val !== '' ? Number(val) : null
      dirtyFields[`${judgeNumber}_${limit.type}`] = false
      emit('mark', {
        drawId: props.draw.id,
        data: { judges: [{ judgeNumber, marks: [{ type: limit.type, score }] }] }
      })
    }

    function averagePreviewValues() {
      const result = {}
      props.scoreLimits.forEach((limit) => {
        const nums = judgeNumbersList().map((judgeNumber) => {
          const v = judgeValue(judgeNumber, limit.type)
          return v === '' || v == null ? 0 : Number(v)
        })
        const avg = nums.length > 0 ? nums.reduce((a, b) => a + b, 0) / nums.length : 0
        result[limit.type] = toDisplayScore(avg)
      })
      return result
    }

    function orderedScoreInputs() {
      const containers = Array.from(document.querySelectorAll('.draws-table__col.--inputs'))
      return containers.reduce((result, container) => {
        const inputs = Array.from(container.querySelectorAll('[data-score-input]:not([disabled])'))
        inputs.sort((a, b) => {
          const judgeDiff = Number(a.dataset.judgeIndex) - Number(b.dataset.judgeIndex)
          if (judgeDiff !== 0) return judgeDiff
          return Number(a.dataset.limitIndex) - Number(b.dataset.limitIndex)
        })
        return result.concat(inputs)
      }, [])
    }

    function focusNextScoreInput(currentEl, reverse, selectAll) {
      const all = orderedScoreInputs()
      const idx = all.indexOf(currentEl)
      if (idx === -1) return
      const next = reverse ? all[idx - 1] : all[idx + 1]
      if (next) {
        next.focus()
        if (selectAll) next.select()
      }
    }

    function onScoreKeydown(event) {
      const { key, shiftKey } = event
      if (key !== 'Enter' && key !== 'Tab') return

      if (key === 'Tab' && shiftKey) {
        event.preventDefault()
        focusNextScoreInput(event.target, true, true)
        return
      }

      event.preventDefault()
      if (key === 'Enter') {
        event.target.blur()
      }
      focusNextScoreInput(event.target, false, key === 'Tab')
    }

    return {
      memberName,
      hasMember,
      totalLabel,
      formatScore,
      eventLabel,
      scoreDisabled,
      isFieldPending,
      isPaid,
      moveOptions,
      commitOptionsWithNull,
      currentCommitOption,
      onCommitChange,
      onMoveSelect,
      maskScore,
      emitMark,
      onScoreKeydown,
      judgeNumbersList,
      judgeValue,
      averagePreviewValues
    }
  }
})
</script>

<style scoped>
.draws-table__totals-group {
  display: flex;
  flex-wrap: nowrap;
}

.row-label {
  @media (min-width: 1441px) {
    display: none !important;
  }
}

.multi-judge-row .draws-table__col.--names {
  align-self: flex-start;
}

.multi-judge-row .draws-table__totals-group {
  align-self: stretch;
}

.multi-judge-row .draws-table__col.--total {
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
}

.multi-judge-row .draws-table__total-value {
  font-size: 16px;
  line-height: 18px;
}

.draws-table__row:not(.multi-judge-row) .draws-table__col.--total {
  align-self: center;
  transform: translateY(-8px);
}

.draws-table__col.--inputs {
  flex-direction: column;
  align-items: stretch;
}

.judge-scores-row {
  display: flex;
  align-items: flex-end;
  width: 100%;
}

.judge-scores-row + .judge-scores-row {
  margin-top: 6px;
}

.judge-scores-row--average {
  font-weight: 600;
  border-top: 1px solid #d8d8d8;
  margin-top: 4px;
  padding-top: 4px;
}

.judge-scores-row--average .draws-table__cell {
  padding-top: 2px;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/Draws/DrawRow.vue